import React, { useState, useEffect, useCallback, Suspense } from 'react';

// LegacyMythicFootball removed as part of Phase 1 Legacy Item System cleanup
import MythicFootball from './MythicFootball';
import ComponentViewer from './dev-tools/ComponentViewer';
import DebugLoginPage from './pages/DebugLoginPage';
import AuthenticationScreen from './components/auth/AuthenticationScreen';
import BetaKeyRequest from './components/auth/BetaKeyRequest';
import BetaKeyValidation from './components/auth/BetaKeyValidation';
import UserRegistration from './components/auth/UserRegistration';
import LeagueSelection from './components/league/LeagueSelection';
import MyTeams from './pages/MyTeamsPage';
import StorePage from './pages/Store/StorePage';
import ShopPage from './pages/Store/ShopPage';
import StoreCategoryPage from './pages/Store/StoreCategoryPage';
import LeagueSignUp from './pages/LeagueSignUpPage';
import TeamPage from './pages/TeamPage';
import SeasonTeamPage from './pages/SeasonTeamPage';
import PlayerPage from './pages/PlayerPage';
import GearPage from './pages/GearPage';
import SeasonPlayerUnavailable from './pages/SeasonPlayerUnavailable';
import SeasonStandingsPage from './pages/SeasonStandingsPage';
import StandingsPage from './pages/StandingsPage';
import LeaderboardPage from './pages/LeaderboardPage';
import SplashPage from './pages/SplashPage';
import BossBattlesPage from './pages/BossBattlesPage';
import BattleDetailsPage from './pages/BattleDetailsPage';
import BossBattlesUnavailable from './pages/BossBattlesUnavailable';
import AccountPage from './pages/AccountPage';
import PrivateLeagueCodeModal from './components/league/PrivateLeagueCodeModal';
import LeagueCodeSuccessModal from './components/league/LeagueCodeSuccessModal';
import TeamNameModal from './components/league/TeamNameModal';
import AccountButton from './components/common/AccountButton';
import { getDraftableLeagues, draftTeam, draftTeamWithTemplate, createPrivateLeague, createPrivateLeagueWithTemplate, joinLeague, getDraftStatus } from './DatabaseAPI';
import { createSeasonLeague, joinSeasonLeague, deriveSeasonLeagueName } from './api/seasonApi';
import AuthGuard from './auth/AuthGuard';
import { useBossBattlesFeatureFlag, BOSS_BATTLES_VISIBILITY } from './api/useBossBattlesFeatureFlag';
import { useAuth } from './auth/AuthContext';
import { shouldUseDebugUI } from './auth/authUtils';
import { describeUser } from './auth/authLogging';
import ClerkStatusBootstrap from './auth/ClerkStatusBootstrap';
import { captureInviteKeyFromUrl, hasPendingInvite, clearStoredInvite } from './auth/inviteKey';
// Analytics imports - using dedicated Analytics module
import analytics from './analytics/Analytics';
// API base URL — resolves to backend correctly in both dev and prod
import { API_BASE } from './api/transport';
// SEASON-VS-DAILY ROUTING lives in this file. `GET /api/v2/auth/teams/` returns
// `mode` ('WEEKLY' | 'SEASON') per team, which is the ONLY thing that
// identifies a season league client-side. handleTeamClick() records it in
// sessionStorage under `selectedLeagueMode`, and the 'teamPage' branch renders
// SeasonTeamPage for 'SEASON' and TeamPage for 'WEEKLY'. A MISSING or
// unrecognised value THROWS at both points — `mode` is a required serializer
// field, so its absence is a contract failure, not a hint that the team is
// weekly. Rendering the wrong roster screen would point a season team at
// weekly-only endpoints the backend rejects outright.

// ---------------------------------------------------------------------------
// DURABLE TEAM CONTEXT — survives losing the browser session
// ---------------------------------------------------------------------------
//
// THE BUG THIS EXISTS FOR (2026-08-16, PR review):
// the draft is RESUMABLE across sessions — `currentScreen` and `draftTeamId`
// both live in localStorage, so closing the tab mid-draft and reopening the app
// puts the user straight back into their packs. But the team context
// (`selectedTeamId`/`selectedLeagueId`/`selectedLeagueMode`) was written ONLY to
// sessionStorage, which that new session does not have.
//
// The mode therefore read as absent on a resumed SEASON draft, and
// `resolveSeasonMode()` treats absent as not-season (correctly — see its own
// comment). So on completion the season hand-off never fired, the weekly GEAR
// page rendered, and it called the team-stats endpoint that
// assert_weekly_league() rejects for season leagues: an error screen with no
// route back to the campaign. Exactly the dead end the hand-off was built to
// remove, reached by a different door.
//
// THE RULE: any state that a RESUMABLE flow depends on must be as durable as
// the flow itself. Mixing lifetimes across one logical unit is the defect —
// this is the third instance in this feature (the others being the restored
// `teamPage` and `gearPage` routes), so the whole context is written together
// and rehydrated together rather than patched per-key.
//
// It is stored keyed BY TEAM so a stale context can never be applied to a
// different team; see rehydrateTeamContext().
const DURABLE_TEAM_CONTEXT_KEY = 'durableTeamContext';

/**
 * Mirror the session team context into localStorage so a resumed draft (a new
 * browser session) can recover it. Call this everywhere the sessionStorage
 * trio is written.
 */
function persistTeamContext({ teamId, leagueId, leagueMode }) {
    if (!teamId || !leagueId || !leagueMode) {
        throw new Error(
            '❌ persistTeamContext requires teamId, leagueId and leagueMode — got ' +
            `${JSON.stringify({ teamId, leagueId, leagueMode })}. Persisting a partial ` +
            'context is how the mode went missing on resumed drafts in the first place.'
        );
    }
    sessionStorage.setItem('selectedTeamId', teamId);
    sessionStorage.setItem('selectedLeagueId', leagueId);
    sessionStorage.setItem('selectedLeagueMode', leagueMode);
    localStorage.setItem(
        DURABLE_TEAM_CONTEXT_KEY,
        JSON.stringify({ teamId: String(teamId), leagueId: String(leagueId), leagueMode })
    );
}

/**
 * Restore the durable context into sessionStorage after a lost session.
 *
 * ONLY applies when the stored context matches the team the resumed draft is
 * actually for (`draftTeamId`, written by DraftPage/SequentialPackSystem). A
 * user who drafted team A yesterday and team B today must not have team A's
 * league/mode applied to team B — that would mis-route the roster screen, the
 * precise class of failure the mode guard exists to prevent.
 *
 * Never overwrites a session that already has a mode: a live session is always
 * more current than this mirror.
 */
function rehydrateTeamContext() {
    if (sessionStorage.getItem('selectedLeagueMode')) {
        return; // live session wins
    }
    const raw = localStorage.getItem(DURABLE_TEAM_CONTEXT_KEY);
    if (!raw) {
        return;
    }

    let stored;
    try {
        stored = JSON.parse(raw);
    } catch (error) {
        console.error('⚠️ AppRouter: durable team context is unparseable — discarding it', error);
        localStorage.removeItem(DURABLE_TEAM_CONTEXT_KEY);
        return;
    }

    if (!stored || !stored.teamId || !stored.leagueId || !stored.leagueMode) {
        console.error('⚠️ AppRouter: durable team context is incomplete — discarding it', stored);
        localStorage.removeItem(DURABLE_TEAM_CONTEXT_KEY);
        return;
    }

    // Must belong to the draft we are actually resuming.
    const resumingDraftTeamId = localStorage.getItem('draftTeamId');
    if (resumingDraftTeamId && String(resumingDraftTeamId) !== String(stored.teamId)) {
        console.warn(
            `⚠️ AppRouter: durable team context is for team ${stored.teamId} but the resumable ` +
            `draft is for team ${resumingDraftTeamId} — discarding the stale context rather ` +
            'than applying another team’s league/mode.'
        );
        localStorage.removeItem(DURABLE_TEAM_CONTEXT_KEY);
        return;
    }

    console.log(
        `♻️ AppRouter: restoring team context after a lost session — team ${stored.teamId}, ` +
        `league ${stored.leagueId}, mode ${stored.leagueMode}`
    );
    sessionStorage.setItem('selectedTeamId', stored.teamId);
    sessionStorage.setItem('selectedLeagueId', stored.leagueId);
    sessionStorage.setItem('selectedLeagueMode', stored.leagueMode);
}

/** Drop the mirror once the flow that needed it is over. */
function clearDurableTeamContext() {
    localStorage.removeItem(DURABLE_TEAM_CONTEXT_KEY);
}

// Runs at module load, BEFORE any route reads the mode, so a restored
// `newPackSelection`/`teamPage`/`gearPage` screen sees a complete context.
rehydrateTeamContext();

// URL-BASED BETA INVITES — `/signup#invite=GRUP_XXXXXXXX`.
//
// Also at module load, and for a second, security-flavoured reason: the key is
// scrubbed from the visible URL in the same tick the app boots, so it is never
// left in the address bar while React mounts, and analytics' own page-view call
// (which reads `window.location`) cannot capture it. Deferring this into a
// component effect would widen that window for no benefit.
//
// Capture only STORES the key — it never validates or consumes it. The user
// still presses the button on the beta-key screen.
captureInviteKeyFromUrl();

/**
 * The screen an UNAUTHENTICATED visitor should land on.
 *
 * SINGLE SOURCE OF TRUTH, deliberately: two separate effects decide this — the
 * initial-auth routing effect and the logout effect — and the second one runs
 * immediately after the first for a visitor who was never logged in. When they
 * disagreed, the later one silently won, which is exactly how invite links got
 * bounced back to the login form. Any new landing rule belongs HERE, not in
 * either effect.
 *
 * Someone arriving on `/signup#invite=...` asked to redeem a key, so they get
 * the beta-key screen rather than a login form for an account they don't have
 * yet. Read from storage rather than the URL so it also survives the refresh
 * that no longer carries the fragment.
 *
 * Uses `hasPendingInvite()` — NOT a key-only check. A malformed or non-shared
 * link deliberately stores no key, only an error marker; routing on the key
 * alone dropped those visitors on the plain login screen where the "that link
 * didn't look right" banner could never render. Someone who clicked an invite
 * link must always be told what happened to it.
 */
function resolveUnauthenticatedMode() {
    if (hasPendingInvite()) {
        console.log('AppRouter: Pending invite found, routing to beta key entry');
        return 'betaKeyValidation';
    }
    return 'realLogin';
}

// Baseball sport shell — lazy loaded, only fetched when user navigates to /baseball
const BaseballApp = React.lazy(() => import('./games/baseball/BaseballApp'));

/**
 * Neutral placeholder shown while the `mode_boss_battle` flag read is in flight
 * on a Boss Battles route.
 *
 * DELIBERATELY SAYS NOTHING ABOUT BOSS BATTLES. It is displayed before we know
 * whether the user may see the mode at all, so naming it would leak the very
 * thing the flag hides, and it must not resolve into either the real page or
 * the unavailable screen looking like a state change.
 */
function BossBattlesRouteLoading() {
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: '#1a202c',
            color: 'white',
            fontSize: '18px'
        }}>
            <div>Loading…</div>
        </div>
    );
}

function AppRouterContent() {
    const {
        user: authenticatedUser,
        isAuthenticated,
        isLoading,
        logout,
    } = useAuth();
    const [appMode, setAppMode] = useState('initializing'); // 'initializing', 'login', 'old', 'debug', 'realLogin', 'betaKeyRequest', 'betaKeyValidation', 'userRegistration', 'leagueSelection', 'splashPage', 'myTeams', 'leagueSignUp', 'teamPage', 'playerPage', 'opponentTeamPage', 'leaderboardPage', 'accountPage'
    const [user, setUser] = useState(null);
    // Which store category is being viewed. Persisted so a refresh on a
    // category page restores the right one (appMode alone only remembers 'storeCategory').
    const [storeCategory, setStoreCategory] = useState(
        () => localStorage.getItem('storeCategory') || 'skins'
    );
    const [leagueId, setLeagueId] = useState(null);
    const [leagueName, setLeagueName] = useState(null);
    const [initialPage, setInitialPage] = useState(null);
    const [betaKey, setBetaKey] = useState(null);
    const [hasHandledInitialAuth, setHasHandledInitialAuth] = useState(false);
    const [selectedOpponentTeam, setSelectedOpponentTeam] = useState(null);
    const [logoutError, setLogoutError] = useState(null);
    // Shop V1 — nav dot state: true when user has a free pull available
    const [shopFreePullAvailable, setShopFreePullAvailable] = useState(false);

    // Pre-fetch shop state on auth to populate the notification dot before the user visits the shop.
    // Uses a raw fetch (not getShopState()) so failures are silently ignored — the dot simply
    // stays off when the shop is disabled or the network fails. Never throws or alerts.
    useEffect(() => {
        if (!isAuthenticated) return;
        let cancelled = false;
        fetch(`${API_BASE}/v2/shop/`, { credentials: 'include' })
            .then(r => {
                if (!r.ok) return null; // feature disabled or error — ignore silently
                return r.json();
            })
            .then(data => {
                if (cancelled || !data) return;
                const printerOffer = (data.offers ?? []).find(o => o.offer_key === 'player_printer');
                const hasFree =
                    printerOffer?.next_entitlement === 'DAILY_FREE' ||
                    printerOffer?.next_entitlement === 'PUNCH_FREE';
                setShopFreePullAvailable(hasFree);
            })
            .catch(() => { /* silent — dot stays off */ });
        return () => { cancelled = true; };
    }, [isAuthenticated]);

    // Baseball route detection — checked before football routing
    const isBaseballRoute = window.location.pathname.startsWith('/baseball');

    // Feature flag state — checked on mount via backend API
    const [isBaseballEnabled, setIsBaseballEnabled] = React.useState(false);
    const [baseballFlagLoaded, setBaseballFlagLoaded] = React.useState(false);

    React.useEffect(() => {
        // Check baseball feature flag on app load
        // API_BASE resolves to http://localhost:8000/api in dev, /api in prod
        fetch(`${API_BASE}/platform/feature-flags/sport_baseball/`)
            .then(r => {
                if (!r.ok && r.status !== 404) {
                    throw new Error(`Feature flag check failed with status ${r.status}`);
                }
                return r.json();
            })
            .then(data => {
                // INTERNAL state = visible to internal users (all logged-in for now)
                // ACTIVE = fully live for everyone
                setIsBaseballEnabled(data.is_internal === true || data.is_active === true);
            })
            .catch(() => {
                // If flag check fails, disable baseball routes (fail-safe)
                setIsBaseballEnabled(false);
            })
            .finally(() => {
                setBaseballFlagLoaded(true);
            });
    }, []);

    // BOSS BATTLES ROLLOUT GATE — `mode_boss_battle`, DISABLED by default.
    //
    // Boss Battles ("Fields of Valhalla") is a MODE: it sits above the sports and
    // is deliberately not coupled to football or baseball. This read is therefore
    // sport-agnostic and must stay that way — it is one AllowAny GET, it does not
    // touch the baseball branch below, and nothing about it may be made
    // conditional on the active sport.
    //
    // Hidden while UNKNOWN, so no Boss Battles surface can flash in and then
    // disappear. Both Boss Battles routes below consume this; the entry points
    // that live inside pages read the same hook themselves.
    const bossBattlesFlag = useBossBattlesFeatureFlag(authenticatedUser);

    // Screen persistence for page refresh
    const SCREEN_STATE_KEY = 'currentScreen';
    
    // Enhanced setAppMode that saves state for persistence AND tracks navigation
    const setAppModeWithPersistence = useCallback((mode, previousMode = null) => {
        console.log('AppRouter: Setting app mode with persistence:', mode);
        
        // Track navigation analytics
        if (mode && mode !== 'initializing') {
            analytics.trackPageView(mode, previousMode || appMode, 'navigation');
            if (previousMode || appMode) {
                analytics.trackNavigationAction('page_change', previousMode || appMode, mode);
            }
        }
        
        setAppMode(mode);
        
        // Save current screen state for refresh persistence (except initializing states)
        // Also skip saving for debug users - they should always return to debug login
        if (mode && mode !== 'initializing' && mode !== 'realLogin' && !shouldUseDebugUI(authenticatedUser)) {
            localStorage.setItem(SCREEN_STATE_KEY, mode);
            console.log('AppRouter: Saved screen state for persistence:', mode);
        } else if (shouldUseDebugUI(authenticatedUser)) {
            console.log('AppRouter: Debug user - not saving screen state for persistence');
        }
    }, [authenticatedUser, SCREEN_STATE_KEY, appMode]);
    
    // Function to restore screen state on refresh
    const restoreScreenState = useCallback(() => {
        try {
            const savedScreen = localStorage.getItem(SCREEN_STATE_KEY);
            console.log('AppRouter: Attempting to restore screen state:', savedScreen);
            
            if (savedScreen && isAuthenticated) {
                // For debug users, they should go back to debug login on refresh
                // Don't restore any fake login states - debug is for testing only
                if (shouldUseDebugUI(authenticatedUser)) {
                    console.log('AppRouter: Debug user - clearing saved state and routing to debug login');
                    localStorage.removeItem(SCREEN_STATE_KEY); // Clear any saved state
                    return false; // Don't restore, use default debug routing
                }
                
                // For regular users, restore their saved state
                console.log('AppRouter: Restoring to saved screen:', savedScreen);
                setAppMode(savedScreen);
                return true;
            }
        } catch (error) {
            console.error('AppRouter: Error restoring screen state:', error);
        }
        return false;
    }, [SCREEN_STATE_KEY, isAuthenticated, authenticatedUser]);
    
    
    // Modal states for league management
    const [showPrivateLeagueModal, setShowPrivateLeagueModal] = useState(false);
    const [showTeamNameModal, setShowTeamNameModal] = useState(false);
    const [showLeagueCodeSuccessModal, setShowLeagueCodeSuccessModal] = useState(false);
    const [teamNameModalContext, setTeamNameModalContext] = useState(null);
    const [createdLeague, setCreatedLeague] = useState(null);
    const [selectedLeague, setSelectedLeague] = useState(null);

    const handleSetNewUIMode = (enabled, selectedTeam, leagueData) => {
        if (enabled) {
            console.log('AppRouter: handleSetNewUIMode called with:', { enabled, selectedTeam, leagueData });
            
            // Set the league data immediately before navigating
            if (leagueData) {
                setLeagueId(leagueData.id);
                setLeagueName(leagueData.name);
            }
            setUser(selectedTeam);
            
            // Mirror the old login logic exactly - route to pack selection if packs available
            if (selectedTeam && selectedTeam.packs > 0) {
                setAppModeWithPersistence('newDraft'); // Goes to pack selection page
            } else {
                setAppModeWithPersistence('newTeam');
            }
        }
    };

    const handleSetDebugMode = (enabled) => {
        if (enabled) {
            setAppModeWithPersistence('debugComponents');
        }
    };

    const handleSetRealLoginMode = () => {
        // Don't persist login screens
        setAppMode('realLogin');
    };

    const handleRequestBetaKey = () => {
        setAppMode('betaKeyRequest');
    };

    const handleBetaKeyFlow = () => {
        setAppMode('betaKeyValidation');
    };

    // Handle INITIAL authentication state changes only
    useEffect(() => {
        // Log shape, never content (PR2 Slice F4): authenticatedUser carries
        // email/username/public_id, and this runs on every auth state change.
        console.log('AppRouter: auth effect', {
            isLoading,
            hasHandledInitialAuth,
            isAuthenticated,
            ...describeUser(authenticatedUser)
        });
        
        if (isLoading) {
            setAppMode('initializing');
            setHasHandledInitialAuth(false);
            return;
        }

        // Only handle initial authentication routing, don't interfere with user navigation
        if (!hasHandledInitialAuth) {
            console.log('AppRouter: Handling initial authentication state');
            
            if (isAuthenticated && authenticatedUser) {
                console.log('AppRouter: User authenticated', describeUser(authenticatedUser));

                // AN INVITE SCANNED BY A SIGNED-IN USER IS DEAD ON ARRIVAL.
                //
                // They already have an account, so the key is not redeemable by
                // them — it is only consumable at REGISTRATION. Previously it sat
                // in sessionStorage until logout and then hijacked the next
                // unauthenticated flow in this tab: the classic shape is a signed-in
                // user scanning a friend's card to pass it along, then later
                // logging out, and being dumped into a beta-key screen carrying a
                // key they never meant to redeem themselves.
                //
                // Dropping it here keeps the invite's lifetime inside the one flow
                // that can actually use it. The friend opens the link in their own
                // browser, which is the intended path anyway.
                if (hasPendingInvite()) {
                    console.log(
                        'AppRouter: Discarding invite — user is already signed in ' +
                        'and cannot redeem a beta key.'
                    );
                    clearStoredInvite();
                }
                
                // Try to restore previous screen first
                const restoredScreen = restoreScreenState();
                
                if (!restoredScreen) {
                    // Identify user for analytics using Django User.id (for ALL authenticated users)
                    // Identity is sent to analytics, never printed to the console.
                    console.log('AppRouter: Identifying user for analytics', {
                        ...describeUser(authenticatedUser),
                        hasHandledInitialAuth
                    });
                    console.log('🔍 AppRouter DEBUG: isAuthenticated:', isAuthenticated);
                    console.log('🔍 AppRouter DEBUG: restoredScreen:', restoredScreen);
                    
                    analytics.identifyUser(authenticatedUser.id, {
                        username: authenticatedUser.username,
                        // REMOVED: email - PostHog was using this as distinct_id instead of our User.id
                        is_staff: authenticatedUser.is_staff,
                        is_debug_user: shouldUseDebugUI(authenticatedUser),
                        first_login: new Date().toISOString(),
                        profile_type: shouldUseDebugUI(authenticatedUser) ? 'debug_user' : 'regular_user',
                        django_user_id: authenticatedUser.id // Store as property for reference
                    });
                    
                    // No saved screen, use default routing
                    if (shouldUseDebugUI(authenticatedUser)) {
                        console.log('AppRouter: Routing admin user to debug UI (staying authenticated)');
                        setAppModeWithPersistence('login', 'authentication'); // Use legacy login with all button handlers - but user stays authenticated
                    } else {
                        console.log('AppRouter: Routing regular user directly to MyTeams');
                        setAppModeWithPersistence('myTeams', 'authentication');
                    }
                }
            } else {
                console.log('AppRouter: User not authenticated, showing login');
                // Clear any saved screen state when not authenticated
                localStorage.removeItem(SCREEN_STATE_KEY);
                setAppMode(resolveUnauthenticatedMode());
            }
            
            setHasHandledInitialAuth(true);
        }
    }, [isAuthenticated, authenticatedUser, isLoading, hasHandledInitialAuth, restoreScreenState, setAppModeWithPersistence, SCREEN_STATE_KEY]);
    
    // SEPARATE EFFECT: Always identify authenticated users for analytics (regardless of hasHandledInitialAuth)
    useEffect(() => {
        console.log('🔍 AppRouter DEBUG: User identification effect triggered');
        console.log('🔍 AppRouter DEBUG: isLoading:', isLoading);
        console.log('AppRouter: analytics identify check', {
            isAuthenticated,
            userPresent: !!authenticatedUser
        });
        
        if (!isLoading && isAuthenticated && authenticatedUser) {
            console.log('AppRouter: Identifying user for analytics', describeUser(authenticatedUser));
            
            analytics.identifyUser(authenticatedUser.id, {
                username: authenticatedUser.username,
                // REMOVED: email - PostHog was using this as distinct_id instead of our User.id
                is_staff: authenticatedUser.is_staff,
                is_debug_user: shouldUseDebugUI(authenticatedUser),
                profile_type: shouldUseDebugUI(authenticatedUser) ? 'debug_user' : 'regular_user',
                django_user_id: authenticatedUser.id // Store as property for reference
            });
        }
    }, [isAuthenticated, authenticatedUser, isLoading]);
    
    // Handle authentication logout (when user becomes unauthenticated)
    //
    // CAREFUL: this ALSO fires for a visitor who was never logged in, because
    // the initial-auth effect above sets `hasHandledInitialAuth` to true on its
    // unauthenticated branch — so on the very next render both conditions here
    // are satisfied and this effect re-asserts the landing screen. That is why
    // it must resolve the mode the SAME way the initial branch does: a plain
    // `setAppMode('realLogin')` here silently overwrote the invite route and
    // sent people holding a valid invite link back to the login form.
    useEffect(() => {
        if (!isLoading && !isAuthenticated && hasHandledInitialAuth) {
            console.log('AppRouter: Unauthenticated, resetting to landing screen');
            // Clear saved screen state on logout
            localStorage.removeItem(SCREEN_STATE_KEY);
            setAppMode(resolveUnauthenticatedMode());
            // Don't reset hasHandledInitialAuth to avoid infinite loop
        }
    }, [isAuthenticated, isLoading, hasHandledInitialAuth]);


    const handleRealLogin = (user) => {
        console.log('AppRouter: handleRealLogin called with user:', user);
        
        // Check if this is a debug fake login
        if (user && user.debugMode && user.actualDebugUser) {
            console.log('AppRouter: Debug fake login detected, mode:', user.debugMode);
            
            // Track debug login
            analytics.trackAuthUIAction('debug_login_success', 'login', user.debugMode);
            
            // Set up the fake login context
            setUser(user);
            setLeagueId(user.leagueId);
            setLeagueName(user.leagueName);
            
            // Navigate based on debug mode
            if (user.debugMode === 'myTeams') {
                setAppModeWithPersistence('myTeams');
            } else if (user.debugMode === 'debugComponents') {
                setAppModeWithPersistence('debugComponents');
            }
            return;
        }
        
        // Regular login flow - Login is now handled by AuthProvider, this is just for navigation
        if (shouldUseDebugUI(user)) {
            console.log('AppRouter: handleRealLogin routing to debug login');
            analytics.trackAuthUIAction('admin_login_success', 'realLogin');
            setAppModeWithPersistence('login'); // Use 'login' mode, not 'legacyDebug'
        } else {
            console.log('AppRouter: handleRealLogin routing directly to MyTeams');
            analytics.trackAuthUIAction('user_login_success', 'realLogin');
            setAppModeWithPersistence('myTeams');
        }
    };

    const handleBetaKeyValidated = (key) => {
        // The validated record carries `key_code` — not logged, see platformApi.
        console.log('AppRouter: Beta key validated, advancing to userRegistration');
        setBetaKey(key);
        setAppMode('userRegistration');
    };

    const handleUserRegistered = (user) => {
        // The key has now actually been spent (`BetaKey.use_key()` incremented
        // usage_count server-side), so drop it. Clearing any earlier — on
        // capture, or on reaching the form — would lose the key on a refresh or
        // a failed submit and strand the user; clearing later leaves a spent key
        // prefilling the field for the next signup in this tab.
        clearStoredInvite();

        // User registration will trigger authentication state change via AuthProvider
        // Just navigate to the appropriate mode
        if (shouldUseDebugUI(user)) {
            console.log('AppRouter: handleUserRegistered routing to debug login');
            setAppModeWithPersistence('login'); // Use 'login' mode, not 'legacyDebug'
        } else {
            console.log('AppRouter: handleUserRegistered routing directly to MyTeams');
            setAppModeWithPersistence('myTeams');
        }
    };

    const handleBackToLogin = async () => {
        console.log('AppRouter: handleBackToLogin called');
        
        // Track logout action
        if (authenticatedUser) {
            analytics.trackAuthUIAction('logout_clicked', appMode);
            analytics.trackSessionEnded(0, 0, appMode); // Basic session tracking
        }
        
        // Check if this is a debug user - they should go back to debug login, not logout
        if (authenticatedUser && shouldUseDebugUI(authenticatedUser)) {
            console.log('AppRouter: Debug user going back to debug login screen');
            
            // Clear local app state from fake login
            setUser(null);
            setLeagueId(null);
            setLeagueName(null);
            setBetaKey(null);
            
            // Clear sessionStorage for navigation state
            sessionStorage.removeItem('selectedTeamId');
            sessionStorage.removeItem('selectedLeagueId');
            sessionStorage.removeItem('previousLocation');
            sessionStorage.removeItem('selectedPlayerId');
            
            // Clear screen persistence
            localStorage.removeItem(SCREEN_STATE_KEY);
            
            // Go back to debug login screen (not logout)
            setAppMode('login');
            return;
        }
        
        // For regular users, perform actual logout
        console.log('AppRouter: Regular user - performing actual logout');
        try {
            // Reset analytics before logout
            analytics.resetAnalytics();
            
            await logout();
            console.log('AppRouter: Logout successful');
            setLogoutError(null);
        } catch (error) {
            console.error('AppRouter: Logout error:', error);
            analytics.trackError('logout_failed', error.message, { context: 'AppRouter' });
            setLogoutError(error.message);
        }
        
        // Clear local app state
        setLeagueId(null);
        setLeagueName(null);
        setBetaKey(null);
        
        // Clear sessionStorage for navigation state
        sessionStorage.removeItem('selectedTeamId');
        sessionStorage.removeItem('selectedLeagueId');
        sessionStorage.removeItem('previousLocation');
        sessionStorage.removeItem('selectedPlayerId');
        
        // Clear screen persistence
        localStorage.removeItem(SCREEN_STATE_KEY);
        setAppMode('realLogin');
        
        // The AuthProvider logout will handle setting the correct mode
    };

    const handleBackToRealLogin = () => {
        console.log('AppRouter: handleBackToRealLogin called - preserving authentication state');

        // BACKING OUT ABANDONS THE INVITE — otherwise it is a trap.
        //
        // `resolveUnauthenticatedMode()` sends anyone with a pending invite to
        // the beta-key screen, so leaving the invite in storage meant Back
        // returned to `realLogin` and the very next refresh dragged the user
        // straight back into the invite flow they just chose to leave, with no
        // way out short of clearing site data.
        //
        // Reaching for Back is the clearest statement that the user does not
        // want this key right now; the invite has not been consumed, so nothing
        // of value is lost, and the link can be reopened.
        clearStoredInvite();

        setAppMode('realLogin');
        setBetaKey(null);
        
        // Clear sessionStorage for navigation state
        sessionStorage.removeItem('selectedTeamId');
        sessionStorage.removeItem('selectedLeagueId');
        sessionStorage.removeItem('previousLocation');
        sessionStorage.removeItem('selectedPlayerId');
    };

    const handleBackToAuth = () => {
        console.log('AppRouter: handleBackToAuth called - preserving authentication state');

        // Same reasoning as handleBackToRealLogin: Back from the beta-key screen
        // abandons the pending invite so a refresh cannot re-trap the user in it.
        clearStoredInvite();

        setAppMode('realLogin');
        setBetaKey(null);
        
        // Clear sessionStorage for navigation state
        sessionStorage.removeItem('selectedTeamId');
        sessionStorage.removeItem('selectedLeagueId');
        sessionStorage.removeItem('previousLocation');
        sessionStorage.removeItem('selectedPlayerId');
    };

    // New Team Management Handlers
    const handleShowNewTeam = () => {
        analytics.trackButtonClick('new_team', appMode);
        analytics.trackLeagueUIAction('create_team_clicked');
        setAppModeWithPersistence('leagueSignUp', appMode);
    };

    const handleShowMyTeams = () => {
        analytics.trackButtonClick('my_teams', appMode);
        analytics.trackNavigationAction('my_teams_clicked', appMode, 'myTeams');
        setAppModeWithPersistence('myTeams', appMode);
    };

    // Handler for navigating back to splash page
    const handleBackToSplash = () => {
        console.log('AppRouter: handleBackToSplash - navigating to splash page');
        setAppModeWithPersistence('splashPage');
    };

    // Splash page handlers
    const handleSplashPlayNow = () => {
        console.log('AppRouter: handleSplashPlayNow - navigating to MyTeams');
        analytics.trackButtonClick('play_now', 'splashPage');
        analytics.trackNavigationAction('play_now_clicked', 'splashPage', 'myTeams');
        setAppModeWithPersistence('myTeams', 'splashPage');
    };

    const handleSplashTipDevs = () => {
        console.log('AppRouter: handleSplashTipDevs - tip modal handled by SplashPage');
        analytics.trackButtonClick('tip_devs', 'splashPage');
        // Modal is handled within SplashPage component
    };

    // Team click handler - check draft status on-demand, then navigate appropriately
    const handleTeamClick = async (team) => {
        console.log('AppRouter: Team click handler called with team:', team);
        
        // Track team click
        analytics.trackTeamInteraction('team_selected', team.id, 'myTeams');
        analytics.trackButtonClick('team_card', 'myTeams');
        
        // Real Login teams use league_id (underscore), legacy teams use leagueId (camelCase)
        const teamLeagueId = team.league_id || team.leagueId;
        const teamLeagueName = team.league_name || team.leagueName;
        // `mode` comes from GET /api/v2/auth/teams/ and is the ONLY client-side
        // signal distinguishing a season league from a weekly one.
        //
        // NO DEFAULT. `mode` is a REQUIRED field on UserTeamsSerializer
        // (backend/game/api/serializers/auth_serializers.py — a bare
        // `serializers.CharField()`, no default, not allow_null), so its absence
        // is a RESPONSE-CONTRACT FAILURE, never evidence that the team is
        // weekly. The previous `|| 'WEEKLY'` fallback turned that failure into a
        // silent wrong-screen bug: a malformed SEASON row would store 'WEEKLY',
        // sail past the render-time guard (which only rejects UNRECOGNISED
        // values, not wrongly-defaulted ones), and open the daily roster — which
        // then calls weekly-only endpoints the backend rejects for season teams.
        //
        // Failing loudly here is strictly better: the two screens are
        // incompatible, and guessing which one to show is exactly the class of
        // silent failure this codebase forbids.
        const teamLeagueMode = team.mode || team.leagueMode;
        if (teamLeagueMode !== 'WEEKLY' && teamLeagueMode !== 'SEASON') {
            throw new Error(
                `❌ INVALID TEAM MODE from GET /api/v2/auth/teams/: team ${team.id} ` +
                `("${team.name || team.display_name || 'unnamed'}") has mode=` +
                `${JSON.stringify(teamLeagueMode)}. Expected 'WEEKLY' or 'SEASON'. ` +
                '`mode` is a REQUIRED serializer field and is the only signal that ' +
                'chooses between the season and daily roster screens — it is never ' +
                'defaulted, because guessing would silently open the wrong screen ' +
                'against endpoints that reject the other mode.'
            );
        }

        console.log(
            'AppRouter: Team league info - ID:', teamLeagueId,
            'Name:', teamLeagueName,
            'Mode:', teamLeagueMode
        );
        
        try {
            // Check if team needs to draft (on-demand API call using consolidated endpoint)
            console.log('AppRouter: Checking if team needs to draft...');
            const draftStatus = await getDraftStatus(team.id);
            
            if (draftStatus.needs_draft) {
                // Team needs to draft - set up user data and navigate to pack selection
                console.log('AppRouter: Team needs draft, navigating to pack selection...');
                console.log('AppRouter: Packs remaining:', draftStatus.packs_remaining);

                // THE MODE MUST SURVIVE THE DRAFT. A SEASON team absolutely can
                // reach this branch — a season draft opens once the commissioner
                // starts the league — and when the draft finishes,
                // SequentialPackSystem navigates to the GEAR page, which calls
                // the generic team-stats endpoint. TeamStatsView.get() runs
                // assert_weekly_league() and REJECTS a season league, so without
                // this the completed season draft dead-ended on an error screen
                // with no route to its season roster. Persisting the mode here
                // (not only in the already-drafted branch below) is what lets
                // MythicFootball send a finished season draft back through the
                // season-aware return path.
                // persistTeamContext (not three bare setItem calls): the draft is
                // resumable across sessions, so this context must be too.
                persistTeamContext({
                    teamId: team.id,
                    leagueId: teamLeagueId,
                    leagueMode: teamLeagueMode,
                });

                // Track draft entry
                analytics.trackLeagueUIAction('draft_entry', null, {
                    team_id: team.id,
                    packs_remaining: draftStatus.packs_remaining,
                    league_id: teamLeagueId
                });
                
                // Set up user data for NewApp - mimic the old login system structure
                const teamUser = {
                    id: team.id,
                    username: authenticatedUser?.username || 'Unknown',
                    packs: draftStatus.packs_remaining
                };
                
                setUser(teamUser);
                setLeagueId(teamLeagueId);
                setLeagueName(teamLeagueName);
                
                // Navigate to pack selection (same as Login(New) logic)
                setAppModeWithPersistence('newPackSelection');
            } else {
                // Team is drafted, navigate to TeamPage as normal
                console.log('AppRouter: Team is drafted, navigating to TeamPage...');
                console.log('AppRouter: Storing teamId:', team.id, 'leagueId:', teamLeagueId);
                
                // Track team page view
                analytics.trackNavigationAction('team_page_opened', 'myTeams', 'teamPage');
                
                // Store only IDs (+ the league mode, which decides WHICH roster
                // screen renders) in sessionStorage per aireadme.txt pattern
                persistTeamContext({
                    teamId: team.id,
                    leagueId: teamLeagueId,
                    leagueMode: teamLeagueMode,
                });
                sessionStorage.setItem('previousLocation', 'myTeams');
                setAppModeWithPersistence('teamPage');
            }
        } catch (error) {
            console.error('AppRouter: Error checking team draft status:', error);
            analytics.trackError('draft_status_check_failed', error.message, { context: 'AppRouter.handleTeamClick' });
            
            // Fallback: navigate to team page (safe default)
            console.log('AppRouter: Falling back to TeamPage due to error...');
            persistTeamContext({
                teamId: team.id,
                leagueId: teamLeagueId,
                leagueMode: teamLeagueMode,
            });
            sessionStorage.setItem('previousLocation', 'myTeams');
            setAppModeWithPersistence('teamPage');
        }
    };
    
    // Handle back navigation from TeamPage
    const handleBackFromTeamPage = () => {
        // Clear sessionStorage when navigating back
        sessionStorage.removeItem('selectedTeamId');
        sessionStorage.removeItem('selectedLeagueId');
        sessionStorage.removeItem('previousLocation');
        // Cleared with the team ids it belongs to: a mode left behind from a
        // previous team would route the NEXT team to the wrong roster screen.
        sessionStorage.removeItem('selectedLeagueMode');
        // Clear the durable mirror TOO, or rehydrateTeamContext() would put the
        // just-cleared mode straight back on the next load — the mirror must
        // never outlive the session value it mirrors.
        clearDurableTeamContext();
        setAppModeWithPersistence('myTeams');
    };

    // Handle TeamPage navigation to different pages
    const handleTeamPageNavigation = (destination) => {
        console.log('AppRouter: handleTeamPageNavigation called with:', destination);
        
        // Track navigation action
        analytics.trackNavigationAction(`team_nav_${destination.toLowerCase()}`, 'teamPage', getDestinationMode(destination));
        
        switch(destination) {
            case 'HOME':
                analytics.trackButtonClick('home_nav', 'teamPage');
                handleBackFromTeamPage(); // HOME goes back to MyTeams
                break;
            case 'LEAGUE':
                // LEAGUE should go to standings (WEEKLY standings — see
                // SEASON_STANDINGS below for why season needs its own).
                console.log('LEAGUE navigation - navigating to standings');
                analytics.trackButtonClick('league_nav', 'teamPage');
                setAppModeWithPersistence('standingsPage', 'teamPage');
                break;
            case 'SEASON_STANDINGS':
                // A SEPARATE destination on purpose. The weekly standingsPage
                // reads League standings (Team.wins/losses + this week's
                // WeeklyTeamStats, zeroed when missing); season standings come
                // from SEALED SeasonTeamWeekResult rows with a pinned tiebreak
                // recipe. Same-looking table, different answer — and the weekly
                // endpoint has no guard that would reject a season league, so
                // reusing it silently showed wrong records.
                console.log('SEASON_STANDINGS navigation - navigating to season standings');
                analytics.trackButtonClick('season_standings_nav', 'teamPage');
                setAppModeWithPersistence('seasonStandingsPage', 'teamPage');
                break;
            case 'TEAM':
                // Emitted by SeasonStandingsPage's tab bar / Back button. The
                // weekly TeamPage never sends this (it IS the team page), so it
                // previously fell through to the unknown-destination branch and
                // did nothing — a dead Back button.
                console.log('TEAM navigation - returning to the team page');
                setAppModeWithPersistence('teamPage');
                break;
            case 'GEAR':
                console.log('GEAR navigation - navigating to gear page');
                analytics.trackButtonClick('gear_nav', 'teamPage');
                setAppModeWithPersistence('gearPage', 'teamPage');
                break;
            case 'PLAYER':
                console.log('PLAYER navigation - navigating to player page');
                analytics.trackButtonClick('player_nav', 'teamPage');
                setAppModeWithPersistence('playerPage', 'teamPage');
                break;
            case 'LEADERBOARD':
                console.log('LEADERBOARD navigation - navigating to leaderboard page');
                setAppModeWithPersistence('leaderboardPage');
                break;
            default:
                console.log('Unknown navigation destination:', destination);
                analytics.trackError('unknown_navigation', `Unknown destination: ${destination}`, { context: 'AppRouter.handleTeamPageNavigation' });
                break;
        }
    };
    
    // Helper function to map navigation destination to app mode
    const getDestinationMode = (destination) => {
        switch(destination) {
            case 'HOME': return 'myTeams';
            case 'LEAGUE': return 'standingsPage';
            case 'SEASON_STANDINGS': return 'seasonStandingsPage';
            case 'GEAR': return 'gearPage';
            case 'PLAYER': return 'playerPage';
            default: return 'unknown';
        }
    };

    // Handle GearPage navigation to different pages (different from TeamPage navigation)
    const handleGearPageNavigation = (destination) => {
        console.log('GearPage navigation:', destination);
        switch(destination) {
            case 'HOME':
                handleBackFromTeamPage(); // HOME goes back to MyTeams (league selection)
                break;
            case 'TEAM':
                setAppModeWithPersistence('teamPage'); // TEAM goes back to team page
                break;
            case 'LEAGUE':
                // LEAGUE should go to standings
                console.log('LEAGUE navigation - navigating to standings');
                setAppModeWithPersistence('standingsPage');
                break;
            case 'SEASON_STANDINGS':
                // Emitted by GearPage's STANDINGS button for a SEASON team.
                // A separate destination on purpose — the weekly standingsPage
                // ranks Team.wins/losses + this week's WeeklyTeamStats and has
                // no guard rejecting a season league, so it renders a
                // plausible but wrong table. See SeasonStandingsPage.
                console.log('SEASON_STANDINGS navigation - navigating to season standings');
                setAppModeWithPersistence('seasonStandingsPage');
                break;
            case 'GEAR':
                // Already on GEAR page, do nothing
                break;
            case 'LEADERBOARD':
                console.log('LEADERBOARD navigation - navigating to leaderboard page');
                setAppModeWithPersistence('leaderboardPage');
                break;
            default:
                console.log('Unknown navigation destination in GearPage:', destination);
                break;
        }
    };

    // Handle StandingsPage navigation (different from TeamPage navigation)
    const handleStandingsPageNavigation = (destination) => {
        console.log('StandingsPage navigation:', destination);
        switch(destination) {
            case 'HOME':
                handleBackFromTeamPage(); // HOME goes back to MyTeams (league selection)
                break;
            case 'TEAM':
                setAppModeWithPersistence('teamPage'); // TEAM goes back to team page
                break;
            case 'LEAGUE':
                // Already on LEAGUE/standings page, do nothing
                break;
            case 'GEAR':
                console.log('GEAR navigation - navigating to gear page');
                setAppModeWithPersistence('gearPage');
                break;
            case 'LEADERBOARD':
                console.log('LEADERBOARD navigation - navigating to leaderboard page');
                setAppModeWithPersistence('leaderboardPage');
                break;
            case 2: // NEW_PAGES.CHARACTER - opponent team viewing
                console.log('CHARACTER navigation - opponent team view');
                setAppModeWithPersistence('opponentTeamPage');
                break;
            default:
                console.log('Unknown navigation destination in StandingsPage:', destination);
                break;
        }
    };

    // Handle OpponentTeamPage navigation (different from other navigations)
    const handleOpponentTeamPageNavigation = (destination) => {
        switch(destination) {
            case 'HOME':
                handleBackFromTeamPage();
                break;
            case 'TEAM':
                // Clear opponent state when navigating back to own team
                localStorage.removeItem('currentScreen');
                sessionStorage.removeItem('opponentTeamId');
                sessionStorage.removeItem('opponentTeamName');
                sessionStorage.removeItem('viewMode');
                sessionStorage.removeItem('playerViewMode');
                // Use setAppMode directly to avoid race condition with persistence
                setAppMode('teamPage');
                localStorage.setItem(SCREEN_STATE_KEY, 'teamPage');
                break;
            case 'LEAGUE':
                setAppModeWithPersistence('standingsPage');
                break;
            case 'GEAR':
                setAppModeWithPersistence('gearPage');
                break;
            case 'PLAYER':
                setAppModeWithPersistence('playerPage');
                break;
            default:
                console.error('AppRouter: Unknown navigation destination in OpponentTeamPage:', destination);
                break;
        }
    };

    // Handle LeaderboardPage navigation
    const handleLeaderboardPageNavigation = (destination) => {
        console.log('LeaderboardPage navigation:', destination);
        switch(destination) {
            case 'HOME':
                handleBackFromTeamPage(); // HOME goes back to MyTeams (league selection)
                break;
            case 'TEAM':
                setAppModeWithPersistence('teamPage'); // TEAM goes back to team page
                break;
            case 'LEAGUE':
                console.log('LEAGUE navigation - navigating to standings');
                setAppModeWithPersistence('standingsPage');
                break;
            case 'GEAR':
                console.log('GEAR navigation - navigating to gear page');
                setAppModeWithPersistence('gearPage');
                break;
            case 'LEADERBOARD':
                // Already on LEADERBOARD page, do nothing
                break;
            default:
                console.log('Unknown navigation destination in LeaderboardPage:', destination);
                break;
        }
    };

    // Handle gear navigation (when clicking on individual player gear)
    const handleGearNavigation = (playerId) => {
        console.log('Gear navigation for player:', playerId);
        // Store the selected player ID for gear page
        sessionStorage.setItem('selectedPlayerId', playerId);
        setAppModeWithPersistence('gearPage');
    };

    // Handle account button click
    const handleAccountClick = () => {
        console.log('AppRouter: Navigating to account page');
        analytics.trackNavigationAction('account_button_clicked', appMode, 'accountPage');
        setAppModeWithPersistence('accountPage', appMode);
    };

    // Handle PlayerPage navigation.
    //
    // PlayerPage speaks ONE uppercase vocabulary to whichever host mounted it —
    // 'LEAGUES' | 'TEAM' | 'STANDINGS' | 'GEAR', plus 'HOME' from its error /
    // no-data recovery screens. It no longer guesses whether it is running under
    // AppRouter or the NewApp draft shell by sniffing sessionStorage; that guess
    // was wrong inside the shell (AppRouter has already written `selectedTeamId`
    // before the shell mounts), which silently killed the GEAR/EQUIP buttons
    // there. Mapping that vocabulary onto app modes is this host's job, below.
    const handlePlayerPageNavigation = (destination) => {
        console.log('PlayerPage navigation:', destination);
        switch(destination) {
            case 'LEAGUES':
                // Clear player selection when going back to MyTeams
                sessionStorage.removeItem('selectedPlayerId');
                sessionStorage.removeItem('playerViewMode');
                setAppModeWithPersistence('myTeams');
                break;
            case 'STANDINGS':
                setAppModeWithPersistence('standingsPage');
                break;
            case 'TEAM':
                setAppModeWithPersistence('teamPage');
                break;
            case 'GEAR':
                setAppModeWithPersistence('gearPage');
                break;
            case 'HOME':
                // PlayerPage's ERROR and no-data screens emit 'HOME' directly
                // (not via handleNavigation). It was unhandled, so the only
                // control on the failure screen did nothing and the user had to
                // reload the browser to escape. Treat it as "back to the team".
                console.log('PlayerPage HOME (error-screen recovery) - returning to the team page');
                sessionStorage.removeItem('selectedPlayerId');
                sessionStorage.removeItem('playerViewMode');
                setAppModeWithPersistence('teamPage');
                break;
            default:
                console.error(
                    'Unknown navigation destination in PlayerPage:', destination,
                    "- expected one of 'LEAGUES', 'TEAM', 'STANDINGS', 'GEAR', 'HOME'."
                );
                analytics.trackError(
                    'unknown_navigation',
                    `Unknown destination: ${destination}`,
                    { context: 'AppRouter.handlePlayerPageNavigation' }
                );
                break;
        }
    };

    /**
     * Join/draft into a public league.
     *
     * @param {number} templateId - League template to draft under (required)
     * @param {number} [entryCost] - Entry fee, for modal display only. Pass it ONLY
     *        when it came from real backend league data. There is deliberately NO
     *        default: an omitted cost must stay `undefined` so TeamNameModal prints
     *        "—". Defaulting to 0 would advertise a FREE entry the backend never
     *        agreed to — it charges league.get_entry_cost() regardless of what the
     *        modal said.
     * @param {Object} [selectedArena] - The EXACT arena card the user clicked
     *        ({ league_id, league_name, current_teams, max_teams }). When
     *        provided, that league is joined verbatim — we must NOT fall back to
     *        "first draftable league", or clicking the 2nd listed arena would
     *        place the user in the 1st whenever several arenas share a template.
     */
    const handleDraftTeam = async (templateId, entryCost, selectedArena = null) => {
        console.log('🎯 AppRouter: handleDraftTeam called with template_id:', templateId, 'entryCost:', entryCost ?? '(unknown)', 'selectedArena:', selectedArena);
        
        // VALIDATE REQUIRED TEMPLATE ID - ERROR LOUDLY IF MISSING
        if (!templateId) {
            const error = new Error('❌ MISSING TEMPLATE_ID: handleDraftTeam called without template_id parameter');
            analytics.trackError('missing_template_id', error.message, { context: 'AppRouter.handleDraftTeam' });
            throw error;
        }
        
        // Track league creation action
        analytics.trackLeagueUIAction('public_draft_started', null, {
            template_id: templateId
        });
        analytics.trackButtonClick('draft_team', 'leagueSignUp');
        
        // EXPLICIT ARENA SELECTION — honour the user's actual choice.
        if (selectedArena) {
            if (!selectedArena.league_id) {
                const error = new Error(
                    `❌ MISSING LEAGUE_ID: handleDraftTeam received a selectedArena without league_id: ${JSON.stringify(selectedArena)}`
                );
                analytics.trackError('missing_league_id', error.message, { context: 'AppRouter.handleDraftTeam' });
                throw error;
            }

            analytics.trackLeagueUIAction('arena_league_selected', null, {
                league_id: selectedArena.league_id,
                template_id: templateId
            });

            setSelectedLeague(null);
            setTeamNameModalContext({
                type: 'draft',
                templateId: templateId,
                leagueId: selectedArena.league_id, // authoritative target league
                leagueInfo: {
                    name: selectedArena.league_name,
                    leagueType: 'Public',
                    currentTeams: selectedArena.current_teams,
                    maxTeams: selectedArena.max_teams,
                    entryFee: entryCost
                }
            });
            setShowTeamNameModal(true);
            return;
        }
        
        try {
            const response = await getDraftableLeagues();
            if (response.leagues && response.leagues.length > 0) {
                // Show league selection - for now, auto-select first available league
                const firstLeague = response.leagues[0];
                setSelectedLeague(firstLeague);
                
                // Track joining existing league
                analytics.trackLeagueUIAction('existing_league_selected', null, {
                    league_id: firstLeague.league_id,
                    template_id: templateId
                });
                
                setTeamNameModalContext({
                    type: 'draft',
                    templateId: templateId, // Store template_id in context
                    leagueId: firstLeague.league_id,
                    leagueInfo: {
                        name: firstLeague.league_name,
                        leagueType: 'Public',
                        currentTeams: firstLeague.current_teams,
                        maxTeams: firstLeague.max_teams,
                        entryFee: entryCost
                    }
                });
                setShowTeamNameModal(true);
            } else {
                // No available leagues, create new one
                analytics.trackLeagueUIAction('new_public_league_creation', null, {
                    template_id: templateId
                });
                
                setTeamNameModalContext({
                    type: 'draft',
                    templateId: templateId, // Store template_id in context
                    leagueInfo: {
                        name: 'New Public League (Auto-Created)',
                        leagueType: 'Public',
                        currentTeams: 0,
                        maxTeams: 12,
                        entryFee: entryCost
                    }
                });
                setShowTeamNameModal(true);
            }
        } catch (error) {
            console.error('Error getting draftable leagues:', error);
            analytics.trackError('draftable_leagues_fetch_failed', error.message, { context: 'AppRouter.handleDraftTeam' });
            
            // Show team name modal anyway for new league creation
            setTeamNameModalContext({
                type: 'draft',
                templateId: templateId, // Store template_id in context
                leagueInfo: {
                    name: 'New Public League (Auto-Created)',
                    leagueType: 'Public',
                    currentTeams: 0,
                    maxTeams: 12,
                    entryFee: entryCost
                }
            });
            setShowTeamNameModal(true);
        }
    };

    /**
     * Open the create-private-league flow.
     *
     * @param {number} [templateId] - OPTIONAL, and normally omitted. Pass a value
     *        ONLY if a caller has a real, user-selected template id from backend
     *        data (e.g. getPrivateLeagueTemplates()). No current caller does — the
     *        redesigned LeagueSignUpPage has no template selector — so the id is
     *        left absent and CreatePrivateLeagueView resolves the current-week
     *        PRIVATE default itself (backend/game/api/views/auth_views.py:781-797).
     * @param {number} [entryCost] - Entry fee for modal display only. Omit when
     *        unknown: it must NOT be defaulted to 0, because 0 is a meaningful
     *        "free" value and the modal must be able to tell "free" from "unknown".
     *
     * WHY THIS NO LONGER DEMANDS A TEMPLATE ID: it used to throw on absence, which
     * forced callers to invent one — the page was literally passing a hardcoded 1,
     * an id that is wrong on most databases (may not exist, may not be PRIVATE, may
     * be another week's row). Absence is now legal and meaningful; a SUPPLIED id is
     * still validated loudly downstream in createPrivateLeagueWithTemplate.
     */
    const handleCreatePrivate = (templateId, entryCost) => {
        console.log('🔒 AppRouter: handleCreatePrivate called with template_id:', templateId ?? '(omitted — backend default)', 'entryCost:', entryCost ?? '(unknown)');

        // Track private league creation action
        analytics.trackLeagueUIAction('private_league_creation_started', null, {
            template_id: templateId ?? null,
            template_source: templateId ? 'caller_supplied' : 'backend_default'
        });
        analytics.trackButtonClick('create_private', 'leagueSignUp');

        setTeamNameModalContext({
            type: 'createPrivate',
            // Carried through ONLY when a caller genuinely supplied one. Undefined
            // here means "let the backend resolve the default" — never a stand-in.
            templateId: templateId,
            leagueInfo: {
                name: 'New Private League',
                leagueType: 'Private',
                currentTeams: 0,
                maxTeams: 12,
                entryFee: entryCost
            }
        });
        setShowTeamNameModal(true);
    };

    /**
     * Open the JOIN flow for an existing, forming season league.
     *
     * Reuses TeamNameModal because the season join endpoint REQUIRES a
     * team_name — the league row alone is not enough to join with.
     *
     * `entryFee: 0` is DELIBERATE AND AUTHORITATIVE, not a placeholder. Season
     * V0 is free by construction: `SeasonLeagueConfig.entry_fee_mythril` is
     * `default=0` with help_text "Always 0 in V0 — free leagues only", and
     * `validators.py` RAISES if it is ever non-zero. The join service never
     * calls `get_entry_cost()` and writes no CurrencyTransaction.
     *
     * (Corrected 2026-08-16: this previously OMITTED the key so the modal
     * printed "—". That is the marker for "we could not load the price", so a
     * user was asked to confirm a free operation with the cost shown as
     * unknown. 0 and unknown are different answers; the modal now renders 0 as
     * "FREE".)
     *
     * @param {Object} league - a row from GET v2/season-leagues/available/
     */
    const handleJoinSeason = (league) => {
        console.log(`🏆 AppRouter: handleJoinSeason for league ${league.league_id} ("${league.name}")`);
        analytics.trackLeagueUIAction('season_league_join_started', league.league_id, {
            team_count: league.team_count,
            teams_joined: league.teams_joined
        });
        analytics.trackButtonClick('join_season', 'leagueSignUp');

        setTeamNameModalContext({
            type: 'joinSeason',
            seasonLeagueId: league.league_id,
            leagueInfo: {
                name: league.name,
                leagueType: league.league_type === 'PRIVATE' ? 'Private' : 'Public',
                currentTeams: league.teams_joined,
                maxTeams: league.team_count,
                // Authoritative zero — see the docblock above. Renders "FREE".
                entryFee: 0,
                // Same reason as createSeason: the modal's "Win up to 700" is a
                // hardcoded literal, and season payouts are the rules version's
                // configurable `placement_rewards`. The available-leagues row
                // carries no reward data at all, so quoting one would advertise
                // a payout THIS campaign may never pay.
                payoutKnown: false,
                // SeasonLeagueJoinService creates the Team with `name` only, so
                // the modal must not collect a profile name it would discard.
                supportsKnownAs: false
            }
        });
        setShowTeamNameModal(true);
    };

    /**
     * Open the CREATE flow for a new season league, from a real template row.
     *
     * The template id is REQUIRED by POST v2/season-leagues/ — there is no
     * current-week default to fall back on the way weekly private creation has
     * one — so it is validated here rather than discovered as a 404 later.
     *
     * @param {Object} template - a row from GET v2/season-league-templates/
     */
    const handleCreateSeason = (template) => {
        if (!template || !template.template_id) {
            throw new Error(
                `❌ MISSING TEMPLATE_ID: handleCreateSeason requires a real season template row, got ${JSON.stringify(
                    template
                )}`
            );
        }
        console.log(`🏆 AppRouter: handleCreateSeason from template ${template.template_id}`);
        analytics.trackLeagueUIAction('season_league_creation_started', null, {
            template_id: template.template_id,
            team_count: template.team_count
        });
        analytics.trackButtonClick('create_season', 'leagueSignUp');

        setTeamNameModalContext({
            type: 'createSeason',
            seasonTemplateId: template.template_id,
            leagueInfo: {
                name: template.name,
                leagueType: 'Public',
                currentTeams: 0,
                maxTeams: template.team_count,
                // AUTHORITATIVE ZERO → renders "FREE". Season V0 is free by
                // construction: SeasonLeagueConfig.entry_fee_mythril is
                // default=0 ("Always 0 in V0 — free leagues only") and
                // season/validators.py RAISES if it is ever non-zero. Creation
                // writes no CurrencyTransaction.
                //
                // Corrected 2026-08-16: previously omitted, which printed "—"
                // — the marker for an unloaded/unknown price. Asking a user to
                // confirm a free action with the cost shown as unknown is the
                // same ambiguity as a fabricated number, in the other
                // direction. Note this is NOT symmetric with `payoutKnown`
                // below: the fee is knowable and known; the payout genuinely
                // is not.
                entryFee: 0,
                // Suppresses the modal's "Win up to 700" row. Season rewards
                // come from the rules version's configurable
                // `placement_rewards`, which do not even exist until the league
                // is created — so that literal would advertise a payout THIS
                // campaign may never pay.
                payoutKnown: false,
                // SeasonLeagueCreationService creates the Team with `name` only
                // — no known_as/hide_known_as columns are written — so the
                // modal hides those inputs instead of collecting values that
                // would be silently dropped (and instead of REQUIRING a profile
                // name, which it previously did).
                supportsKnownAs: false
            }
        });
        setShowTeamNameModal(true);
    };

    /**
     * Join a private league by invite code.
     *
     * @param {string} [leagueCode] - Invite code. Pages that collect the code in
     *        their OWN modal (the redesigned LeagueSignUpPage) pass it here, and we
     *        go straight to team naming. Callers that pass nothing get the legacy
     *        PrivateLeagueCodeModal to collect it first.
     */
    const handleJoinPrivate = (leagueCode) => {
        analytics.trackLeagueUIAction('join_private_league_started');
        analytics.trackButtonClick('join_private', 'leagueSignUp');

        if (typeof leagueCode === 'string' && leagueCode.trim()) {
            // Code already collected upstream — reuse the exact same submit path
            // the legacy modal uses so private joins behave identically.
            return handlePrivateLeagueCodeSubmit(leagueCode.trim());
        }

        setShowPrivateLeagueModal(true);
        return undefined;
    };

    // Modal handlers
    const handlePrivateLeagueCodeSubmit = async (leagueCode) => {
        try {
            console.log('🔍 AppRouter: handlePrivateLeagueCodeSubmit called with:', leagueCode);
            
            console.log('🔍 AppRouter: Setting teamNameModalContext...');
            // Store the league code and show team naming modal (validation happens in team creation step)
            setTeamNameModalContext({
                type: 'joinPrivate',
                leagueCode: leagueCode,
                leagueInfo: {
                    name: 'Private League',
                    leagueType: 'Private',
                    // Everything about the target league is unknown at this point:
                    // we hold only an invite code, and validation/lookup happens
                    // server-side during the join itself. These placeholders are
                    // never rendered by TeamNameModal (it reads name + entryFee
                    // only) — they document the unknown for future readers.
                    currentTeams: '?',
                    maxTeams: '?'
                    // entryFee DELIBERATELY OMITTED — do NOT put a number here.
                    // This used to be `entryFee: 0`, which made the modal quote a
                    // free entry while the backend looked the real league up and
                    // charged league.get_entry_cost() (auth_views.py:570-602).
                    // We have no league object client-side and must not invent a
                    // price; undefined makes TeamNameModal print an explicit "—".
                }
            });
            
            console.log('🔍 AppRouter: Closing PrivateLeagueModal and opening TeamNameModal...');
            // Close private league modal and show team name modal
            setShowPrivateLeagueModal(false);
            setShowTeamNameModal(true);
            
            console.log('🔍 AppRouter: Modal states set - PrivateLeague: false, TeamName: true');
            console.log('🔍 AppRouter: Returning success status');
            return { status: 'success' }; // Return success to close the private league modal
        } catch (error) {
            console.error('❌ AppRouter: Error in handlePrivateLeagueCodeSubmit:', error);
            throw error; // Let the modal handle the error display
        }
    };

    const handleTeamNameSubmit = async (teamName, leagueInfo) => {
        try {
            console.log('🔍 AppRouter: handleTeamNameSubmit called with:', { teamName, leagueInfo, context: teamNameModalContext });
            let response = null;
            
            if (teamNameModalContext.type === 'draft') {
                console.log('🔍 AppRouter: Drafting team in public league');
                
                // VALIDATE TEMPLATE_ID - ERROR LOUDLY IF MISSING
                const templateId = teamNameModalContext.templateId;
                if (!templateId) {
                    throw new Error('❌ MISSING TEMPLATE_ID: Draft team context missing template_id');
                }
                
                console.log('🔍 AppRouter: Using template_id:', templateId);
                
                // The league the user actually chose (e.g. the arena card they
                // clicked) is authoritative. `selectedLeague` is only a legacy
                // fallback for flows that never picked a specific league — it
                // must NEVER override an explicit context.leagueId, or clicking
                // the 2nd arena would join the 1st when they share a template.
                const targetLeagueId = teamNameModalContext.leagueId || selectedLeague?.league_id || null;
                
                // Draft team in public league with template
                if (targetLeagueId) {
                    console.log('🔍 AppRouter: Drafting into league_id:', targetLeagueId);
                    response = await draftTeamWithTemplate({
                        league_id: targetLeagueId,
                        team_name: teamName.name,
                        known_as: teamName.known_as,
                        hide_known_as: teamName.hide_known_as
                    }, templateId);
                } else {
                    console.log('🔍 AppRouter: Creating new public league with template');
                    // Create new public league (backend will auto-create)
                    response = await draftTeamWithTemplate({
                        team_name: teamName.name,
                        known_as: teamName.known_as,
                        hide_known_as: teamName.hide_known_as
                    }, templateId);
                }
            } else if (teamNameModalContext.type === 'createPrivate') {
                console.log('🔍 AppRouter: Creating private league');

                // NO TEMPLATE_ID REQUIRED. Unlike the 'draft' branch above (where the
                // template identifies which public league to join), private creation
                // has no user-facing template choice today, so the id is normally
                // absent and the backend resolves the current-week PRIVATE default.
                // We pass whatever the context holds — undefined stays undefined, and
                // createPrivateLeagueWithTemplate omits the key from the request body
                // entirely rather than sending null.
                const templateId = teamNameModalContext.templateId;
                console.log(
                    templateId
                        ? `🔍 AppRouter: Using caller-supplied template_id: ${templateId}`
                        : '🔍 AppRouter: No template_id — backend resolves the current-week PRIVATE default'
                );

                response = await createPrivateLeagueWithTemplate({
                    league_name: `${teamName.name}'s League`,
                    team_name: teamName.name,
                    known_as: teamName.known_as,
                    hide_known_as: teamName.hide_known_as
                }, templateId);
                
                // 🔍 DIAGNOSTIC LOGGING: Check complete response structure
                console.log('🔍 AppRouter: FULL createPrivateLeagueWithTemplate response:', response);
                console.log('🔍 AppRouter: response.league_code value:', response.league_code);
                console.log('🔍 AppRouter: response.league_code type:', typeof response.league_code);
                console.log('🔍 AppRouter: All response keys:', Object.keys(response));
                
                // Show success modal with league ID
                if (response.league_code) {
                    console.log('✅ AppRouter: Private league created successfully with code:', response.league_code);
                    setCreatedLeague({
                        code: response.league_code,
                        name: response.league_name || `${teamName.name}'s League`
                    });
                    setShowTeamNameModal(false);
                    setShowLeagueCodeSuccessModal(true);
                    return; // Don't navigate to teams yet
                } else {
                    console.error('❌ AppRouter: LEAGUE CODE MISSING! Response does not contain league_code field');
                    console.error('❌ AppRouter: This is why the League Code modal is not showing');
                    // Continue with normal flow for now, but this is the bug
                }
            } else if (teamNameModalContext.type === 'joinPrivate') {
                console.log('🔍 AppRouter: ENTERING JOIN PRIVATE LEAGUE BRANCH');
                console.log('🔍 AppRouter: League code from context:', teamNameModalContext.leagueCode);
                console.log('🔍 AppRouter: Team data:', teamName);
                
                const joinData = {
                    league_code: teamNameModalContext.leagueCode,
                    team_name: teamName.name,
                    known_as: teamName.known_as,
                    hide_known_as: teamName.hide_known_as
                };
                
                console.log('🔍 AppRouter: About to call joinLeague with data:', joinData);
                
                try {
                    response = await joinLeague(joinData);
                } catch (joinError) {
                    console.error('❌ AppRouter: joinLeague API call failed:', joinError);
                    throw joinError; // Re-throw to be caught by outer try-catch
                }
            } else if (
                teamNameModalContext.type === 'createSeason' ||
                teamNameModalContext.type === 'joinSeason'
            ) {
                // SEASON ENDPOINTS RETURN A DIFFERENT SHAPE and are therefore
                // handled entirely inside this branch, with their own early
                // return. The weekly post-processing below keys off
                // `response.status === 'success'`, a field the season API does
                // not emit (create returns the dashboard payload + team_id;
                // join returns {team_id, league_id}). Falling through would
                // land on the "operation failed" console.error despite a
                // successful create/join.
                let seasonTeamId;
                let seasonLeagueId;

                if (teamNameModalContext.type === 'createSeason') {
                    console.log('🏆 AppRouter: creating season league from template',
                        teamNameModalContext.seasonTemplateId);
                    const created = await createSeasonLeague({
                        templateId: teamNameModalContext.seasonTemplateId,
                        // The season API takes league_name and team_name as
                        // separate fields; the creator names their team and the
                        // league is derived from it, matching the weekly
                        // private-creation wording the user already knows.
                        //
                        // Derived via the helper, NOT by inlining
                        // `${name}'s Campaign`: Team.name allows 100 chars but
                        // league_name is also capped at 100, so appending the
                        // suffix to a legal 90–100 char team name produced a
                        // 101–111 char league name that the backend always
                        // rejected. The helper truncates to stay in contract.
                        leagueName: deriveSeasonLeagueName(teamName.name),
                        teamName: teamName.name,
                        leagueType: 'PUBLIC',
                    });
                    seasonTeamId = created.team_id;
                    seasonLeagueId = created.league.id;
                    console.log(
                        `✅ AppRouter: season league ${seasonLeagueId} created, team ${seasonTeamId}`
                    );
                } else {
                    console.log('🏆 AppRouter: joining season league',
                        teamNameModalContext.seasonLeagueId);
                    const joined = await joinSeasonLeague(teamNameModalContext.seasonLeagueId, {
                        teamName: teamName.name,
                    });
                    seasonTeamId = joined.team_id;
                    seasonLeagueId = joined.league_id;
                    console.log(
                        `✅ AppRouter: joined season league ${seasonLeagueId} as team ${seasonTeamId}`
                    );
                }

                setShowTeamNameModal(false);
                setTeamNameModalContext(null);

                // The mode must be persisted BEFORE any navigation decision:
                // the draft is resumable across sessions, and the post-draft
                // return path needs it to route back to the season screen.
                persistTeamContext({
                    teamId: seasonTeamId,
                    leagueId: seasonLeagueId,
                    leagueMode: 'SEASON',
                });
                sessionStorage.setItem('previousLocation', 'myTeams');

                // ASK THE SERVER WHETHER THIS TEAM CAN DRAFT — do not assume.
                //
                // This previously went straight to `teamPage` on the premise
                // that a season draft only opens once the commissioner starts
                // the league. THAT PREMISE WAS FALSE. `serialize_league_dashboard()`
                // sets `can_draft_initial_roster` for status in ('FORMING',
                // 'DRAFTING'), and backend/tests/football/season/
                // test_season_draft_to_regular_season.py:161-192 drafts the
                // creator to completion *while the league is still FORMING*.
                //
                // The symptom was a brand-new manager landing on a roster page
                // with no draft control, who could only discover their draft by
                // backing out to My Teams and re-entering the same team — which
                // worked precisely because handleTeamClick() asks the server via
                // needs_draft. This now takes the SAME path, so both entry
                // points agree instead of one guessing.
                try {
                    const seasonDraftStatus = await getDraftStatus(seasonTeamId);

                    if (seasonDraftStatus.needs_draft) {
                        console.log(
                            '🏆 AppRouter: new season team needs to draft — ' +
                            'entering pack selection'
                        );
                        analytics.trackLeagueUIAction('draft_entry', null, {
                            team_id: seasonTeamId,
                            packs_remaining: seasonDraftStatus.packs_remaining,
                            league_id: seasonLeagueId,
                        });

                        setUser({
                            id: seasonTeamId,
                            username: authenticatedUser?.username || 'Unknown',
                            packs: seasonDraftStatus.packs_remaining,
                        });
                        setLeagueId(seasonLeagueId);
                        setLeagueName(teamName.name);
                        setAppModeWithPersistence('newPackSelection');
                        return;
                    }
                } catch (error) {
                    // Fall through to the season team page. A failed status read
                    // must not strand the user on a blank screen — the team page
                    // renders the real lifecycle state, and re-entering from My
                    // Teams retries this check.
                    console.error(
                        '⚠️ AppRouter: season draft-status check failed; ' +
                        'falling back to the season team page:', error
                    );
                    analytics.trackError(
                        'season_draft_status_check_failed',
                        error.message,
                        { context: 'AppRouter.handleTeamNameSubmit' }
                    );
                }

                // Already drafted (or the check failed): the season team page
                // shows the FORMING/DRAFTING lifecycle state honestly.
                setAppModeWithPersistence('teamPage');
                return;
            }
            
            console.log('AppRouter: Team operation response:', response);
            if (response && response.status === 'success' && response.team_id) {
                console.log('AppRouter: Team operation successful, checking if team needs to draft');
                // Close modals
                setShowTeamNameModal(false);
                setTeamNameModalContext(null);
                
                // Check if team needs to draft (same logic as handleTeamClick)
                getDraftStatus(response.team_id).then(draftStatus => {
                    // This branch is reached ONLY by the weekly create/join
                    // paths — the season create/join branch returns early above
                    // and sets selectedLeagueMode='SEASON' itself. Writing
                    // 'WEEKLY' explicitly (rather than leaving whatever a
                    // previous team left behind) is what stops a stale 'SEASON'
                    // from a prior session routing this weekly team to the
                    // season page.
                    if (draftStatus.needs_draft) {
                        // Team needs to draft - set up user data and navigate to pack selection
                        console.log('AppRouter: New team needs draft, navigating to pack selection');
                        const teamUser = {
                            id: response.team_id,
                            username: authenticatedUser?.username || 'Unknown',
                            packs: draftStatus.packs_remaining
                        };
                        
                        setUser(teamUser);
                        setLeagueId(response.league_id);
                        setLeagueName(response.league_name || 'New Team League');

                        // Same reason as handleTeamClick: the mode must survive
                        // the draft so the post-draft return path can route
                        // correctly.
                        persistTeamContext({
                            teamId: response.team_id,
                            leagueId: response.league_id,
                            leagueMode: 'WEEKLY',
                        });

                        // Navigate to pack selection for draft
                        setAppModeWithPersistence('newPackSelection');
                    } else {
                        // Team doesn't need draft, go directly to team page
                        console.log('AppRouter: New team already drafted, navigating to team page');
                        persistTeamContext({
                            teamId: response.team_id,
                            leagueId: response.league_id,
                            leagueMode: 'WEEKLY',
                        });
                        sessionStorage.setItem('previousLocation', 'myTeams');
                        setAppModeWithPersistence('teamPage');
                    }
                }).catch(error => {
                    console.error('AppRouter: Error checking draft status for new team:', error);
                    // Fallback to myTeams if draft status check fails
                    setAppModeWithPersistence('myTeams');
                });
            } else {
                console.error('AppRouter: Team operation failed or returned unexpected response');
            }
        } catch (error) {
            console.error('AppRouter: Error in handleTeamNameSubmit:', error);
            throw error; // Let the modal handle the error display
        }
    };

    const handleCloseModals = () => {
        setShowPrivateLeagueModal(false);
        setShowTeamNameModal(false);
        setTeamNameModalContext(null);
        setSelectedLeague(null);
    };

    const handleLeagueCodeSuccessClose = () => {
        setShowLeagueCodeSuccessModal(false);
        setCreatedLeague(null);
        setAppModeWithPersistence('myTeams'); // Navigate to teams view
    };

    // Baseball early-exit — must be before ALL football render logic
    // While flag check is in-flight on a /baseball URL, show a neutral loader
    if (isBaseballRoute && !baseballFlagLoaded) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: '#1a202c',
                color: 'white',
                fontSize: '18px'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ marginBottom: '20px', fontSize: '48px' }}>⚾</div>
                    <div>Loading Baseball...</div>
                </div>
            </div>
        );
    }

    if (isBaseballRoute && isBaseballEnabled) {
        return (
            <Suspense fallback={<div style={{padding: '40px', textAlign: 'center'}}>Loading Baseball...</div>}>
                <BaseballApp />
            </Suspense>
        );
    }

    // Show loading state while initializing
    if (appMode === 'initializing') {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: '#1a202c',
                color: 'white',
                fontSize: '18px'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ marginBottom: '20px', fontSize: '48px' }}>🏈</div>
                    <div>Loading Mythic Football...</div>
                </div>
            </div>
        );
    }

    // Render current mode content
    const currentModeContent = (() => {
        console.log('AppRouter: currentModeContent rendering with appMode:', appMode);
        switch (appMode) {
            case 'login':
                return (
                    <DebugLoginPage
                        setUser={setUser}
                        setLeagueId={setLeagueId}
                        setLeagueName={setLeagueName}
                        setPage={(page) => {
                            // Store the initial page and switch to old mode
                            setInitialPage(page);
                            setAppModeWithPersistence('old');
                        }}
                        setNewUIMode={handleSetNewUIMode}
                        setDebugMode={handleSetDebugMode}
                        setRealLoginMode={handleSetRealLoginMode}
                    />
                );
            
            case 'old':
                // Legacy system removed - redirect to new system
                console.log('Legacy system removed, redirecting to new UI');
                return <MythicFootball initialPage="TEAM" user={user} leagueId={leagueId} leagueName={leagueName} />;
            
            case 'newTeam':
                return <MythicFootball initialPage="TEAM" user={user} leagueId={leagueId} leagueName={leagueName} />;
            
            case 'newDraft':
                return <MythicFootball initialPage="DRAFT" user={user} leagueId={leagueId} leagueName={leagueName} />;
            
            case 'newPackSelection':
                return <MythicFootball
                    initialPage="PACK_SELECTION"
                    user={user}
                    leagueId={leagueId}
                    leagueName={leagueName}
                    onComplete={() => {
                        // Clear AppRouter draft state
                        setUser(null);
                        setLeagueId(null);
                        setLeagueName(null);

                        // A SEASON team goes straight to its (season-aware)
                        // team page rather than MyTeams. This is the other half
                        // of the season hand-off in MythicFootball: that shell
                        // calls onComplete() the moment a season team reaches a
                        // weekly-only page, and bouncing it to MyTeams would
                        // make the user click back into the team they were
                        // already in. The ids + mode are already in
                        // sessionStorage, written before the draft began.
                        //
                        // WEEKLY keeps the original MyTeams behaviour exactly.
                        const draftedLeagueMode = sessionStorage.getItem('selectedLeagueMode');
                        const draftedTeamId = sessionStorage.getItem('selectedTeamId');
                        if (draftedLeagueMode === 'SEASON' && draftedTeamId) {
                            console.log(
                                '🏆 AppRouter: season draft complete — routing to the season team page'
                            );
                            sessionStorage.setItem('previousLocation', 'myTeams');
                            setAppModeWithPersistence('teamPage');
                            return;
                        }

                        // Go back to MyTeams after draft completion (original behavior)
                        console.log('AppRouter: Draft completed, navigating to MyTeams');
                        setAppModeWithPersistence('myTeams');
                    }}
                />;
            
            case 'debugComponents':
                return <ComponentViewer setPage={handleBackToLogin} user={user} leagueId={leagueId} leagueName={leagueName} />;
            
            case 'realLogin':
                return (
                    <AuthenticationScreen
                        onLogin={handleRealLogin}
                        onBetaKeyFlow={handleBetaKeyFlow}
                        onRequestBetaKey={handleRequestBetaKey}
                        initialError={logoutError}
                    />
                );
            
            case 'betaKeyRequest':
                return (
                    <BetaKeyRequest
                        onBack={handleBackToAuth}
                    />
                );
            
            case 'betaKeyValidation':
                return (
                    <BetaKeyValidation
                        onValidated={handleBetaKeyValidated}
                        onBack={handleBackToAuth}
                    />
                );
            // NOTE: BetaKeyValidation reads the pending invite itself rather than
            // taking it as a prop. The key is tab-scoped storage, not router
            // state, and threading it through here would mean re-rendering the
            // router to keep a form field in sync with sessionStorage.
            
            case 'userRegistration':
                return (
                    <UserRegistration
                        betaKey={betaKey}
                        onRegistered={handleUserRegistered}
                        onBack={handleBackToRealLogin}
                    />
                );
            
            case 'leagueSelection':
                return (
                    <LeagueSelection
                        user={authenticatedUser}
                        onBack={handleBackToLogin}
                    />
                );
            
            case 'splashPage':
                return (
                    <SplashPage
                        onPlayNow={handleSplashPlayNow}
                        onTipDevs={handleSplashTipDevs}
                        onLogout={handleBackToLogin}
                    />
                );
            
            case 'myTeams':
                return (
                    <MyTeams
                        user={authenticatedUser}
                        onNewTeam={handleShowNewTeam}
                        onBack={handleBackToLogin}
                        onTeamClick={handleTeamClick}
                        shopFreePullAvailable={shopFreePullAvailable}
                        setPage={(destination) => {
                            console.log('MyTeamsPage setPage called with destination:', destination);
                            if (destination === 'LEADERBOARD') {
                                console.log('Navigating to leaderboardPage');
                                setAppModeWithPersistence('leaderboardPage');
                            } else if (destination === 'standingsPage') {
                                console.log('Navigating to standingsPage');
                                setAppModeWithPersistence('standingsPage');
                            } else if (destination === 'bossBattlesPage') {
                                // Gated: MyTeamsPage surfaces no Boss Battles control
                                // today, but this destination is still wired, so it
                                // must respect the same flag as the route it targets.
                                if (!bossBattlesFlag.isVisible) {
                                    console.log('🔒 AppRouter: ignoring bossBattlesPage navigation — the mode is not available');
                                    return;
                                }
                                console.log('Navigating to bossBattlesPage');
                                setAppModeWithPersistence('bossBattlesPage');
                            } else if (destination === 'storePage') {
                                console.log('Navigating to storePage');
                                setAppModeWithPersistence('storePage');
                            } else if (destination === 'accountPage') {
                                console.log('Navigating to accountPage');
                                setAppModeWithPersistence('accountPage');
                            } else {
                                console.warn('Unknown destination from MyTeamsPage:', destination);
                            }
                        }}
                    />
                );
            
            case 'storePage':
                return (
                    <AuthGuard>
                        <ShopPage
                            user={authenticatedUser}
                            setPage={setAppModeWithPersistence}
                            onFreePullAvailable={setShopFreePullAvailable}
                        />
                    </AuthGuard>
                );
            
            case 'storeCategory':
                return (
                    <StoreCategoryPage
                        user={authenticatedUser}
                        category={storeCategory}
                        setPage={(destination) => {
                            if (destination === 'storePage') {
                                setAppModeWithPersistence('storePage');
                            } else if (destination === 'myTeams') {
                                setAppModeWithPersistence('myTeams');
                            } else {
                                console.warn('Unknown destination from StoreCategoryPage:', destination);
                            }
                        }}
                    />
                );
            
            case 'leagueSignUp':
                return (
                    <AuthGuard>
                        {/* Both arena paths are live and each has its OWN entry
                            point, because they hit different backends:
                            · onDraftTeam  — daily. GET v2/leagues/draft-team/
                              (getPublicLeagueTemplates); JOIN passes a real
                              template id, a real-or-undefined cost (never a
                              fabricated 0) and the clicked league when one exists.
                            · onJoinSeason / onCreateSeason — season. The
                              v2/season-leagues/* API. Both collect a team name in
                              the same TeamNameModal and quote no fee, because the
                              season endpoints charge none. */}
                        <LeagueSignUp
                            user={authenticatedUser}
                            onDraftTeam={handleDraftTeam}
                            onCreatePrivate={handleCreatePrivate}
                            onJoinPrivate={handleJoinPrivate}
                            onJoinSeason={handleJoinSeason}
                            onCreateSeason={handleCreateSeason}
                            onMyTeams={handleShowMyTeams}
                            onBack={handleBackToLogin}
                            onBossBattles={() => {
                                // LeagueSignUp hides the Boss Fight card when the flag
                                // is off, so this normally cannot fire — the check is
                                // here because the callback, not the card, is what
                                // actually changes the route.
                                if (!bossBattlesFlag.isVisible) {
                                    console.log('🔒 AppRouter: ignoring onBossBattles — the mode is not available');
                                    return;
                                }
                                setAppModeWithPersistence('bossBattlesPage');
                            }}
                            onAccount={() => setAppModeWithPersistence('accountPage')}
                        />
                        
                        {/* League Management Modals */}
                        <PrivateLeagueCodeModal
                            isOpen={showPrivateLeagueModal}
                            onClose={handleCloseModals}
                            onSubmit={handlePrivateLeagueCodeSubmit}
                        />
                        
                        <TeamNameModal
                            isOpen={showTeamNameModal}
                            onClose={handleCloseModals}
                            onSubmit={handleTeamNameSubmit}
                            leagueInfo={teamNameModalContext?.leagueInfo}
                        />
                        
                        <LeagueCodeSuccessModal
                            isOpen={showLeagueCodeSuccessModal}
                            onClose={handleLeagueCodeSuccessClose}
                            leagueCode={createdLeague?.code}
                            leagueName={createdLeague?.name}
                        />
                    </AuthGuard>
                );
            
            case 'teamPage':
                const selectedTeamId = sessionStorage.getItem('selectedTeamId');
                const selectedLeagueId = sessionStorage.getItem('selectedLeagueId');
                const selectedLeagueMode = sessionStorage.getItem('selectedLeagueMode');

                // ORDER MATTERS: "no team selected" is checked BEFORE the mode.
                //
                // `currentScreen` persists in localStorage but the team context
                // (`selectedTeamId`/`selectedLeagueId`/`selectedLeagueMode`) lives in
                // sessionStorage. Close the tab on teamPage and reopen the app in a
                // fresh session and the screen is restored while the whole team
                // context is gone — an ORDINARY, expected state with an existing
                // recovery UI ("No Team Selected" → My Teams).
                //
                // Validating the mode first turned that ordinary state into a thrown
                // error that crashed the restored app before the recovery UI could
                // render. No team means there is nothing whose mode we need to know.
                //
                // The mode guard below is UNWEAKENED — see why this stays safe:
                // all three keys are written together by handleTeamClick / the season
                // create+join flow and cleared together, so a present team with an
                // absent mode is still a genuine contract failure and still throws.
                const hasTeamContext = Boolean(selectedTeamId);

                if (hasTeamContext) {
                    // NO DEFAULT HERE. The old `|| 'WEEKLY'` made this guard unable to
                    // catch the very failure it existed for: a missing key became
                    // 'WEEKLY' and passed, so only a *corrupted* value was ever
                    // rejected. Absent and unrecognised are both "we do not know which
                    // screen this team needs", and both must fail loudly — the two
                    // screens read different endpoints, so rendering the wrong one
                    // produces backend rejections, not a degraded view.
                    if (selectedLeagueMode !== 'WEEKLY' && selectedLeagueMode !== 'SEASON') {
                        throw new Error(
                            `❌ UNKNOWN LEAGUE MODE in AppRouter teamPage: ${JSON.stringify(selectedLeagueMode)} ` +
                            `for selectedTeamId=${JSON.stringify(selectedTeamId)} ` +
                            '(expected WEEKLY or SEASON, written from the `mode` field of ' +
                            'GET /api/v2/auth/teams/). Refusing to guess a roster screen — the ' +
                            'season and daily pages read different endpoints and offer different ' +
                            'actions. A team id WITH no mode means the team was routed to teamPage ' +
                            'without passing through handleTeamClick or the season create/join ' +
                            'flow — note that a lost SESSION clears the team id too and is handled ' +
                            'as "No Team Selected" above, not here.'
                        );
                    }
                }
                const isSeasonTeam = selectedLeagueMode === 'SEASON';

                return (
                    <AuthGuard>
                        {!hasTeamContext ? (
                            <div style={{
                                padding: '20px',
                                textAlign: 'center',
                                backgroundColor: '#f8f9fa',
                                minHeight: '100vh'
                            }}>
                                <h2>❌ No Team Selected</h2>
                                <p>Please select a team from the My Teams page.</p>
                                <button
                                    onClick={() => setAppModeWithPersistence('myTeams')}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        marginTop: '20px'
                                    }}
                                >
                                    ← Back to My Teams
                                </button>
                            </div>
                        ) : isSeasonTeam ? (
                            <SeasonTeamPage
                                teamId={selectedTeamId}
                                leagueId={selectedLeagueId}
                                setPage={handleTeamPageNavigation}
                            />
                        ) : (
                            <TeamPage
                                teamId={selectedTeamId}
                                leagueId={selectedLeagueId}
                                setPage={handleTeamPageNavigation}
                                setSelectedPlayer={handleGearNavigation}
                                viewMode="own"
                            />
                        )}
                    </AuthGuard>
                );

            
            case 'gearPage':
                const gearTeamId = sessionStorage.getItem('selectedTeamId');
                const gearLeagueId = sessionStorage.getItem('selectedLeagueId');

                // NO MODE GATE HERE — gear is shared Team inventory and GearPage
                // is mode-aware: it loads its player list from the season roster
                // endpoint for a SEASON team and from the weekly stats endpoint
                // otherwise. The season roster now returns `character_sheet_id`,
                // which is the id the whole gear UI is keyed on.
                return (
                    <AuthGuard>
                        <GearPage
                            user={{ id: gearTeamId }}
                            setPage={handleGearPageNavigation}
                            leagueId={gearLeagueId}
                            leagueName="Real Login Team"
                        />
                    </AuthGuard>
                );
            
            case 'playerPage':
                // SEASON TEAMS CANNOT USE PLAYER DETAIL YET — and like gearPage
                // this route is directly reachable, so the SeasonTeamPage card
                // guard alone is not sufficient: `currentScreen` persists in
                // localStorage, so a user whose tab closed on playerPage is
                // restored straight here.
                //
                // PlayerPage loads via getTeamData() → TeamStatsView.get(),
                // which calls assert_weekly_league() unconditionally and rejects
                // season teams; getTeamData() swallows the rejection and returns
                // null, so the user got "Failed to load team data" rather than
                // any indication this is simply not a season feature yet.
                // Blocked by AIDocs/Proposed/backend/season-player-detail-contract.md
                if (sessionStorage.getItem('selectedLeagueMode') === 'SEASON') {
                    console.log(
                        '🔒 AppRouter: player detail is not available for season teams yet — ' +
                        'routing back to the season team page'
                    );
                    return (
                        <AuthGuard>
                            <SeasonPlayerUnavailable
                                onBack={() => setAppModeWithPersistence('teamPage')}
                            />
                        </AuthGuard>
                    );
                }

                const playerViewMode = sessionStorage.getItem('playerViewMode') || 'own';
                const isPlayerOpponentView = playerViewMode === 'opponent';
                
                // Use opponentTeamId if in opponent view, otherwise use selectedTeamId
                const playerPageTeamId = isPlayerOpponentView
                    ? sessionStorage.getItem('opponentTeamId')
                    : sessionStorage.getItem('selectedTeamId');
                    
                const playerPageLeagueId = sessionStorage.getItem('selectedLeagueId');
                const playerPagePlayerId = sessionStorage.getItem('selectedPlayerId');
                
                return (
                    <AuthGuard>
                        {!playerPagePlayerId ? (
                            <div style={{
                                padding: '20px',
                                textAlign: 'center',
                                backgroundColor: '#1a202c',
                                minHeight: '100vh',
                                color: '#f7fafc'
                            }}>
                                <h2>❌ No Player Selected</h2>
                                <p>Please select a player from the Team page.</p>
                                <button
                                    onClick={() => setAppModeWithPersistence('teamPage')}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#4299e1',
                                        color: '#1a202c',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        marginTop: '20px'
                                    }}
                                >
                                    ← Back to Team
                                </button>
                            </div>
                        ) : (
                            <PlayerPage
                                playerId={playerPagePlayerId}
                                teamId={playerPageTeamId}
                                leagueId={playerPageLeagueId}
                                onNavigate={handlePlayerPageNavigation}
                                viewMode={playerViewMode}
                            />
                        )}
                    </AuthGuard>
                );
                
            case 'seasonStandingsPage':
                return (
                    <AuthGuard>
                        <SeasonStandingsPage
                            leagueId={sessionStorage.getItem('selectedLeagueId')}
                            teamId={sessionStorage.getItem('selectedTeamId')}
                            setPage={handleTeamPageNavigation}
                        />
                    </AuthGuard>
                );

            case 'standingsPage':
                const standingsTeamId = sessionStorage.getItem('selectedTeamId');
                const standingsLeagueId = sessionStorage.getItem('selectedLeagueId');

                // A SEASON team must never reach the weekly standings screen.
                // Like gearPage, this route is directly reachable from a restored
                // session, so the SeasonTeamPage tab alone is not a sufficient
                // gate. StandingsPage reads League standings — Team.wins/losses
                // plus this week's WeeklyTeamStats, zeroed when absent — which
                // for a season league is a plausible but WRONG table, and no
                // backend guard rejects it. Send them to the real one.
                if (sessionStorage.getItem('selectedLeagueMode') === 'SEASON') {
                    console.log(
                        '🔀 AppRouter: season team on the weekly standings route — ' +
                        'redirecting to season standings'
                    );
                    return (
                        <AuthGuard>
                            <SeasonStandingsPage
                                leagueId={standingsLeagueId}
                                teamId={standingsTeamId}
                                setPage={handleTeamPageNavigation}
                            />
                        </AuthGuard>
                    );
                }

                return (
                    <AuthGuard>
                        <StandingsPage
                            user={{ id: standingsTeamId }}
                            setPage={handleStandingsPageNavigation}
                            leagueId={standingsLeagueId}
                            leagueName="Real Login Team"
                            setSelectedOpponentTeam={(team) => {
                                // Store opponent team for viewing
                                console.log('Selected opponent team:', team);
                                setSelectedOpponentTeam(team);
                            }}
                        />
                    </AuthGuard>
                );
            
            case 'opponentTeamPage':
                const opponentStandingsTeamId = sessionStorage.getItem('selectedTeamId');
                const opponentStandingsLeagueId = sessionStorage.getItem('selectedLeagueId');
                const opponentTeamId = sessionStorage.getItem('opponentTeamId');
                const opponentTeamName = sessionStorage.getItem('opponentTeamName');

                return (
                    <AuthGuard>
                        {!opponentTeamId ? (
                            <div style={{
                                padding: '20px',
                                textAlign: 'center',
                                backgroundColor: '#f8f9fa',
                                minHeight: '100vh'
                            }}>
                                <h2>❌ No Opponent Team Selected</h2>
                                <p>Please select an opponent team from the standings page.</p>
                                <button
                                    onClick={() => setAppModeWithPersistence('standingsPage')}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        marginTop: '20px'
                                    }}
                                >
                                    ← Back to Standings
                                </button>
                            </div>
                        ) : (
                            <TeamPage
                                teamId={opponentTeamId}
                                leagueId={opponentStandingsLeagueId}
                                setPage={handleOpponentTeamPageNavigation}
                                setSelectedPlayer={handleGearNavigation}
                                viewMode="opponent"
                            />
                        )}
                    </AuthGuard>
                );
            
            case 'leaderboardPage': {
                // leaderboardLeagueId is unrelated to the self-highlight fix below —
                // LeaderboardPage still accepts it as a `leagueId` prop, kept as-is.
                const leaderboardLeagueId = sessionStorage.getItem('selectedLeagueId');

                // PR1 FIX: the self-highlight on the leaderboard must compare the
                // AUTHENTICATED USER's public_id, not a team id. The previous code
                // passed `user={{ id: leaderboardTeamId }}` — `leaderboardTeamId` is
                // sessionStorage['selectedTeamId'], a TEAM id, not a user identifier
                // at all. That bug predates PR1 (it was comparing a team id to a user
                // id already); PR1's leaderboard payload change to public_id-only just
                // made the breakage visible, since `user.public_id` was never populated
                // by that team-id object either.
                //
                // The real authenticated user (with its real public_id) is already
                // available here via useAuth() as `authenticatedUser` — see its use for
                // PostHog identification above. AuthGuard only renders this route's
                // children when `isAuthenticated` is true, and AuthProvider always sets
                // `isAuthenticated` and `user` together (never one without the other —
                // see checkCachedAuth/login/setAuthenticatedUser), so authenticatedUser
                // is guaranteed non-null by the time this case renders.
                //
                // A present authenticatedUser with a MISSING public_id is therefore a
                // genuine contract failure (GET /api/v2/auth/me stopped returning it, or
                // AuthContext dropped it), not an ordinary state — so this fails loudly
                // rather than silently rendering a leaderboard where nothing ever
                // highlights (the previous defensive `user.public_id && ...` behavior).
                if (!isLoading && isAuthenticated && !authenticatedUser?.public_id) {
                    throw new Error(
                        '❌ MISSING public_id ON AUTHENTICATED USER: LeaderboardPage requires ' +
                        'authenticatedUser.public_id (from GET /api/v2/auth/me, added in PR1) ' +
                        'for the self-highlight comparison against the leaderboard\'s public_id ' +
                        `rows, but got: ${JSON.stringify(authenticatedUser)}. This means either ` +
                        'the backend stopped returning public_id on the auth/me payload, or ' +
                        'AuthContext/AuthProvider dropped it somewhere on the way to this route.'
                    );
                }

                return (
                    <AuthGuard>
                        <LeaderboardPage
                            user={authenticatedUser?.public_id ? { public_id: authenticatedUser.public_id } : null}
                            setPage={handleLeaderboardPageNavigation}
                            leagueId={leaderboardLeagueId}
                            leagueName="Real Login Team"
                        />
                    </AuthGuard>
                );
            }
            
            // BOSS BATTLES ROUTES — both gated by `mode_boss_battle`.
            //
            // Hiding the entry points is NOT sufficient: `currentScreen` persists
            // in localStorage, so a user whose tab closed on either screen is
            // restored straight here, and both are reachable from a bookmark or a
            // hand-typed URL. The same guard therefore sits on the route itself,
            // exactly as the season gates on `playerPage`/`standingsPage` do.
            //
            // UNKNOWN renders a neutral loader rather than the page: drawing the
            // real page first would fire the gated API calls and flash Boss
            // Battles content at a user who may not be allowed to see it.
            case 'bossBattlesPage':
                if (bossBattlesFlag.visibility === BOSS_BATTLES_VISIBILITY.UNKNOWN) {
                    return <AuthGuard><BossBattlesRouteLoading /></AuthGuard>;
                }
                if (!bossBattlesFlag.isVisible) {
                    console.log('🔒 AppRouter: Boss Battles is not available — showing the unavailable screen');
                    return (
                        <AuthGuard>
                            <BossBattlesUnavailable
                                onBack={() => setAppModeWithPersistence('myTeams')}
                            />
                        </AuthGuard>
                    );
                }
                return (
                    <AuthGuard>
                        <BossBattlesPage setPage={setAppModeWithPersistence} />
                    </AuthGuard>
                );

            case 'battleDetailsPage':
                if (bossBattlesFlag.visibility === BOSS_BATTLES_VISIBILITY.UNKNOWN) {
                    return <AuthGuard><BossBattlesRouteLoading /></AuthGuard>;
                }
                if (!bossBattlesFlag.isVisible) {
                    console.log('🔒 AppRouter: battle details are not available — showing the unavailable screen');
                    return (
                        <AuthGuard>
                            {/* Back goes to My Teams, not the battle list: that list is
                                gated too, so returning to it would just bounce the user
                                to this same screen. */}
                            <BossBattlesUnavailable
                                onBack={() => setAppModeWithPersistence('myTeams')}
                            />
                        </AuthGuard>
                    );
                }
                return (
                    <AuthGuard>
                        <BattleDetailsPage setPage={setAppModeWithPersistence} />
                    </AuthGuard>
                );
            
            case 'accountPage':
                return (
                    <AuthGuard>
                        <AccountPage
                            user={authenticatedUser}
                            setPage={setAppModeWithPersistence}
                            onLogout={handleBackToLogin}
                        />
                    </AuthGuard>
                );
            
            default:
                return (
                    <AuthenticationScreen
                        onLogin={handleRealLogin}
                        onBetaKeyFlow={handleBetaKeyFlow}
                        onRequestBetaKey={handleRequestBetaKey}
                    />
                );
        }
    })();

    // Determine if AccountButton should be shown
    // Show on authenticated pages, but not on login/auth screens or pages with their own header
    const shouldShowAccountButton = isAuthenticated && ![
        'initializing',
        'realLogin',
        'betaKeyRequest',
        'betaKeyValidation',
        'userRegistration',
        'login',
        'myTeams', // MyTeamsPage has its own user avatar in the header
        'storePage', // StorePage has its own user avatar in the header
        'accountPage' // AccountPage has its own user avatar in the header
    ].includes(appMode);

    // Return main content with AccountButton overlay when authenticated
    return (
        <div>
            {shouldShowAccountButton && (
                <AccountButton onClick={handleAccountClick} />
            )}
            {currentModeContent}
        </div>
    );
}

// Main AppRouter component with AuthProvider wrapper
function AppRouter() {
    return (
        <ClerkStatusBootstrap>
            <AppRouterContent />
        </ClerkStatusBootstrap>
    );
}

export default AppRouter;
